import React, { useState, useEffect } from 'react';
import { Form, Button, Message, Grid, Input, Label, Icon } from 'semantic-ui-react';

const GenericCRUForm = ({
                            data,
                            fields = [],
                            onSave,
                            onCancel,
                            mode = 'create',
                            loading = false,
                            error = null,
                            buttonLabels = {
                                genericCancelLabel: 'Cancel',
                                update: { saveLabel: 'Save Changes', cancelLabel: 'Cancel' },
                                create: { saveLabel: 'Create', cancelLabel: 'Cancel' }
                            }
                        }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [newChild, setNewChild] = useState('');

    useEffect(() => {
        console.log('buttonLabels', buttonLabels, mode);
        if (data) {
            const flattenedData = fields.reduce((acc, field) => {
                if (field.path) {
                    const value = field.path.split('.').reduce((obj, key) => obj?.[key], data);
                    if (field.type === 'date' && value) {
                        console.log('Processing incoming date:', value);
                        const date = new Date(value);
                        acc[field.name] = new Date(date.getTime() + date.getTimezoneOffset() * 60000)
                            .toISOString()
                            .split('T')[0];
                        console.log('Processed date:', acc[field.name]);
                    } else {
                        acc[field.name] = value;
                    }
                } else {
                    if (field.type === 'date' && data[field.name]) {
                        console.log('Processing direct date:', data[field.name]);
                        const date = new Date(data[field.name]);
                        acc[field.name] = new Date(date.getTime() + date.getTimezoneOffset() * 60000)
                            .toISOString()
                            .split('T')[0];
                        console.log('Processed direct date:', acc[field.name]);
                    } else if (field.type === 'childList') {
                        acc[field.name] = data[field.name] || [];
                    } else {
                        acc[field.name] = data[field.name];
                    }
                }
                return acc;
            }, {});
            console.log('Flattened data:', flattenedData);
            setFormData(flattenedData);
        }
    }, [data, fields]);

    const validateField = (field, value) => {
        const { validation } = field;
        if (!validation) return '';

        if (validation.required && !value && value !== false) {
            return `${field.label} is required`;
        }
        if (validation.pattern && !validation.pattern.test(String(value)) && value) {
            return validation.message || `Invalid ${field.label}`;
        }
        if (validation.custom) {
            return validation.custom(value);
        }
        if (field.type === 'childList' && validation.maxLength && value.length > validation.maxLength) {
            return `Maximum ${validation.maxLength} children allowed`;
        }
        return '';
    };

    const handleChange = (field, value) => {
        console.log('handleChange called with:', {
            fieldName: field.name,
            fieldType: field.type,
            value: value
        });

        if (field.type === 'date') {
            console.log('Raw date value:', value);
            setFormData(prev => {
                const newState = { ...prev, [field.name]: value };
                console.log('New form state:', newState);
                return newState;
            });
        } else {
            setFormData(prev => ({ ...prev, [field.name]: value }));
        }

        const error = validateField(field, value);
        setErrors(prev => ({
            ...prev,
            [field.name]: error
        }));
    };

    const handleAddChild = (field) => {
        if (newChild.trim()) {
            const currentChildren = formData[field.name] || [];
            const updatedChildren = [...currentChildren, { name: newChild.trim() }];

            handleChange(field, updatedChildren);
            setNewChild('');
        }
    };

    const handleRemoveChild = (field, indexToRemove) => {
        const currentChildren = formData[field.name] || [];
        const updatedChildren = currentChildren.filter((_, index) => index !== indexToRemove);
        handleChange(field, updatedChildren);
    };

    const handleSave = (e) => {
        e.preventDefault();
        const newErrors = {};
        fields.forEach(field => {
            const error = validateField(field, formData[field.name]);
            if (error) newErrors[field.name] = error;
        });

        if (Object.keys(newErrors).length === 0) {
            const transformedData = fields.reduce((acc, field) => {
                if (field.path) {
                    const pathParts = field.path.split('.');
                    let current = acc;
                    pathParts.forEach((part, index) => {
                        if (index === pathParts.length - 1) {
                            if (field.type === 'date' && formData[field.name]) {
                                console.log('Transforming date for save:', formData[field.name]);
                                current[part] = formData[field.name];
                            } else {
                                current[part] = formData[field.name];
                            }
                        } else {
                            current[part] = current[part] || {};
                            current = current[part];
                        }
                    });
                } else {
                    if (field.type === 'date' && formData[field.name]) {
                        console.log('Transforming direct date for save:', formData[field.name]);
                        acc[field.name] = formData[field.name];
                    } else {
                        acc[field.name] = formData[field.name];
                    }
                }
                return acc;
            }, { ...data });

            console.log('Final transformed data:', transformedData);
            onSave(transformedData);
        } else {
            setErrors(newErrors);
        }
    };

    const renderChildList = (field) => {
        const children = formData[field.name] || [];
        const fieldError = errors[field.name];

        return (
            <Form.Field error={!!fieldError}>
                <label>{field.label}</label>
                <div className="child-list-container">
                    <div className="child-input-container">
                        <Input
                            value={newChild}
                            onChange={(e) => setNewChild(e.target.value)}
                            placeholder="Enter child's name (ordered oldest to youngest)"
                            action
                        >
                            <input />
                            <Button
                                type="button"
                                icon="plus"
                                onClick={() => handleAddChild(field)}
                                disabled={!newChild.trim()}
                            />
                        </Input>
                    </div>
                    <div className="children-list">
                        {children.map((child, index) => (
                            <Label key={index} className="child-item">
                                {child.name}
                                <Icon
                                    name="delete"
                                    onClick={() => handleRemoveChild(field, index)}
                                />
                            </Label>
                        ))}
                    </div>
                    {fieldError && (
                        <Label basic color='red' pointing>
                            {fieldError}
                        </Label>
                    )}
                </div>
            </Form.Field>
        );
    };

    const renderField = (field) => {
        const isDisabled = mode === 'view' || field.disabled;
        const value = formData[field.name] ?? '';
        const fieldError = errors[field.name];

        const commonProps = {
            id: field.name,
            name: field.name,
            label: field.label,
            disabled: isDisabled,
            error: fieldError ? { content: fieldError } : null,
            required: field.validation?.required,
            value: value
        };

        switch (field.type) {
            case 'childList':
                return renderChildList(field);
            case 'date':
                return (
                    <Form.Input
                        {...commonProps}
                        type="date"
                        onChange={(e, { value }) => {
                            console.log('Date input change:', {
                                inputValue: value,
                                rawEventValue: e.target.value,
                                timeStamp: e.timeStamp
                            });
                            handleChange(field, e.target.value);
                        }}
                    />
                );
            case 'select':
                return (
                    <Form.Select
                        {...commonProps}
                        options={field.options}
                        onChange={(_, { value }) => handleChange(field, value)}
                        placeholder={`Select ${field.label}`}
                        multiple={field.multiple}
                        search
                        selection
                    />
                );
            case 'multiselect':
                return (
                    <Form.Select
                        {...commonProps}
                        options={field.options}
                        onChange={(_, { value }) => handleChange(field, value)}
                        placeholder={`Select ${field.label}`}
                        multiple
                        search
                        selection
                    />
                );
            case 'checkbox':
                return (
                    <Form.Checkbox
                        label={field.label}
                        checked={!!value}
                        onChange={(_, { checked }) => handleChange(field, checked)}
                        disabled={isDisabled}
                        style={{ marginTop: '0.5rem' }}
                    />
                );
            case 'textarea':
                return (
                    <Form.TextArea
                        {...commonProps}
                        onChange={(_, { value }) => handleChange(field, value)}
                        rows={field.rows || 3}
                    />
                );
            case 'html':
                return (
                    <Form.Field>
                        <label>{field.label}</label>
                        <div
                            className="html-content-wrapper"
                            style={{
                                padding: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                minHeight: '100px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: value }}
                        />
                    </Form.Field>
                );
            default:
                return (
                    <Form.Input
                        {...commonProps}
                        type={field.type || 'text'}
                        onChange={(_, { value }) => handleChange(field, value)}
                    />
                );
        }
    };

    const renderGroupedFields = () => {
        let currentGroup = [];
        const groupedFields = [];

        fields.forEach((field, index) => {
            if (field.type === 'checkbox' && field.name.includes('Unlisted')) {
                // Add checkbox to previous field's group
                const previousField = currentGroup[currentGroup.length - 1];
                if (previousField) {
                    currentGroup.push(field);
                    // If this is the last field or the next field starts a new group, push current group
                    if (index === fields.length - 1 || !fields[index + 1]?.name.includes('Unlisted')) {
                        groupedFields.push([...currentGroup]);
                        currentGroup = [];
                    }
                }
            } else {
                // If we have a pending group, push it
                if (currentGroup.length > 0) {
                    groupedFields.push([...currentGroup]);
                    currentGroup = [];
                }
                // Start new group with current field
                currentGroup = [field];
                // Push single field group if next field is not a related checkbox
                if (index === fields.length - 1 || !fields[index + 1]?.name.includes('Unlisted')) {
                    groupedFields.push([...currentGroup]);
                    currentGroup = [];
                }
            }
        });

        return (
            <Grid columns={2} stackable>
                {groupedFields.map((group, groupIndex) => (
                    <Grid.Column key={groupIndex} width={8}>
                        {group.map(field => (
                            <div key={field.name} style={{ marginBottom: '0.25rem' }}>
                                {renderField(field)}
                            </div>
                        ))}
                    </Grid.Column>
                ))}
            </Grid>
        );
    };

    return (
        <Form onSubmit={handleSave} loading={loading} error={!!error}>
            {error && <Message error content={error} />}

            {renderGroupedFields()}

            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                <Button type='button' onClick={onCancel}>
                    {buttonLabels.genericCancelLabel}
                </Button>
                {mode !== 'view' && (
                    <Button
                        primary
                        type='submit'
                        loading={loading}
                        disabled={loading}
                    >
                        {mode === 'create' ? buttonLabels.create.saveLabel : buttonLabels.update.saveLabel}
                    </Button>
                )}
            </div>

            <style jsx>{`
                .child-list-container {
                    width: 100%;
                }

                .child-input-container {
                    display: flex;
                    gap: 0.5rem;
                    width: 100%;
                }

                .child-input-container :global(.ui.input) {
                    flex: 1;
                }

                .children-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    margin-top: 0.5rem;
                }

                .child-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    background-color: #f0f0f0;
                    border-radius: 4px;
                }
            `}</style>
        </Form>
    );
};

export default GenericCRUForm;